// src/features/mocktests/WritingSection.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Dialog,
  Button,
  TextField,
  Backdrop,
  Card,
  IconButton,
  Badge,
  Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import { 
  Visibility,
  VisibilityOff,
  CheckRounded as CheckRoundedIcon,
  AccessTime as AccessTimeIcon,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import {
  useCreateWritingAnswerMutation,
  useUploadWritingImagesMutation,
  useLazyGetTestAttemptResultsQuery,
} from "../mockTestSlice";
import NotesModal from "./NotesModal";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import HtmlRenderer from "./HtmlRenderer";

/* ---------------------------------------------------------------
   Header Toggle Icon Component
--------------------------------------------------------------- */

const HeaderToggleIcon = ({ isHeaderVisible, onToggle }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        py: 1,
        bgcolor: 'rgba(247, 247, 247, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(157, 34, 53, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 999,
      }}
    >
      <Tooltip 
        title={isHeaderVisible ? "Hide Header" : "Show Header"} 
        placement="bottom"
        arrow
      >
        <IconButton
          onClick={onToggle}
          sx={{
            background: 'linear-gradient(135deg, #9D2235 0%, #7a182a 100%)',
            color: 'white',
            width: 40,
            height: 40,
            boxShadow: '0px 4px 16px rgba(157, 34, 53, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7a182a 0%, #5d1220 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0px 6px 20px rgba(157, 34, 53, 0.4)',
            },
            '&:active': {
              transform: 'translateY(0px)',
            },
          }}
        >
          {isHeaderVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Add custom scrollbar styling
const scrollbarStyles = `
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #CBD5E0 #F7FAFC;
  }
  
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #F7FAFC;
    border-radius: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #CBD5E0;
    border-radius: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #A0AEC0;
  }
  
  /* Ensure responsive layout */
  .writing-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    width: 100vw;
    max-width: 100vw;
    overflow-x: hidden;
  }
`;

// Add style tag to head of document with better error handling
if (typeof document !== "undefined" && typeof window !== "undefined") {
  // Remove existing style if it exists
  const existingStyle = document.getElementById("writing-section-styles");
  if (existingStyle) {
    existingStyle.remove();
  }

  const styleEl = document.createElement("style");
  styleEl.id = "writing-section-styles";
  styleEl.textContent = scrollbarStyles;
  document.head.appendChild(styleEl);
}

/* ---------------------------------------------------------------
   Enhanced Gradient Countdown (Ring + Pie + Center Icon)
--------------------------------------------------------------- */
const EnhancedCountdown = ({
  size = 56,
  stroke = 5,
  progress = 0, // 0..100
  showPie = true, // show inside pie fill
  showRing = true, // show outer progress ring
  idSuffix = "writing",
}) => {
  const p = Math.min(100, Math.max(0, progress));
  const finished = p >= 100;

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c;
  const offset = c * (1 - p / 100);

  // ------- PIE PATH (sector fill) -------
  const angle = (p / 100) * Math.PI * 2;
  const cx = size / 2;
  const cy = size / 2;
  const x = cx + r * Math.cos(-Math.PI / 2 + angle);
  const y = cy + r * Math.sin(-Math.PI / 2 + angle);
  const largeArc = angle > Math.PI ? 1 : 0;
  const piePath =
    p <= 0
      ? ""
      : `
    M ${cx} ${cy}
    L ${cx} ${cy - r}
    A ${r} ${r} 0 ${largeArc} 1 ${x} ${y}
    Z
  `;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`grad-${idSuffix}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9D2235" />
            <stop offset="100%" stopColor="#5d1220" />
          </linearGradient>
          <radialGradient id={`soft-${idSuffix}`} cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="rgba(157,34,53,0.10)" />
            <stop offset="100%" stopColor="rgba(157,34,53,0.0)" />
          </radialGradient>
        </defs>

        {/* Soft background plate for a card-ish look */}
        <circle cx={cx} cy={cy} r={r} fill={`url(#soft-${idSuffix})`} />

        {/* Track */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(157,34,53,0.18)"
            strokeWidth={stroke}
          />
        )}

        {/* Pie fill from center (sector) */}
        {showPie && p > 0 && (
          <path d={piePath} fill={`url(#grad-${idSuffix})`} />
        )}

        {/* Progress Ring on top */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={`url(#grad-${idSuffix})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}

        {/* Subtle glow when finished */}
        {finished && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#9D2235"
            strokeOpacity="0.25"
            strokeWidth={2}
          />
        )}
      </svg>

      {/* Center icon */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 180ms ease",
          transform: finished ? "scale(1.05)" : "scale(1)",
        }}
      >
        {finished ? (
          <CheckRoundedIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        ) : (
          <AccessTimeIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        )}
      </Box>
    </Box>
  );
};

export default forwardRef(function WritingSection(
  {
    writingData,
    answer,
    onAnswerChange,
    onTimeUp,
    onSubmit,
    timer = undefined,
    mode = "simulation",
    hideSubmitButton = false,
    submissionType = "computer", // new prop: "computer" | "paper"
    studentAttemptId = "", // new prop for API
    writingTestId = "", // new prop for writing test ID
    isAiBased = false, // new prop for evaluation type
    onResultsReceived = null, // new prop for handling results callback
    shouldFetchResults = true, // new prop to control whether to fetch results
    startTime = null, // ISO string from API
    endTime = null, // ISO string from API
    onEndTimeAdjustment = null, // Callback to notify parent of adjusted end time
    onBack = null, // Function to handle back navigation
    isLastQuestion = false, // Whether this is the last question in practice mode
    onFinish = null, // Function to handle finish action
    updateTestAttempt = null, // Function to update test attempt (for simulation mode)
    testId = null, // Test ID for browser back navigation
    bundleId = null, // Bundle ID for browser back navigation
    isHeaderVisible = true, // New prop to control notes button visibility
    // New props for footer control
    onConfirmSubmit = null, // Function to handle confirm submit from parent
    footerProps = null, // Object containing footer-related state and functions
    // Writing task navigation props
    writingTasks = [], // Array of writing tasks
    currentTaskIndex = 0, // Current active task index
    onTaskChange = null, // Function to handle task navigation
    taskAnswers = {}, // Object containing answers for each task
    // Submission status flags
    isWritingSubmitted = false, // If true in simulation mode, disable everything
    isIeltsWritingSubmited = { isTask1Submitted: false, isTask2Submitted: false }, // Task-level submission status
  },
  ref
) {
  // Comprehensive logging for debugging
  console.log("🔍 WritingSection: Component rendered with props:");
  console.log("📝 writingTasks:", writingTasks);
  console.log("📝 writingTasks.length:", writingTasks?.length);
  console.log("📝 currentTaskIndex:", currentTaskIndex);
  console.log("📝 mode:", mode);
  console.log("📝 writingData:", writingData);
  console.log("📝 taskAnswers:", taskAnswers);
  console.log("📝 onTaskChange callback exists:", typeof onTaskChange === 'function');
  console.log("📝 isWritingSubmitted:", isWritingSubmitted);
  console.log("📝 isIeltsWritingSubmited:", isIeltsWritingSubmited);

  // Determine if current task's textarea should be disabled
  // In simulation mode: if isWritingSubmitted is true, disable everything
  // In practice mode: check individual task submission status
  const isCurrentTaskSubmitted = (() => {
    if (mode === "simulation" && isWritingSubmitted) {
      return true; // All tasks disabled in simulation mode if writing is submitted
    }
    // For practice mode, check individual task submission status based on actual task number
    const currentTask = writingTasks[currentTaskIndex];
    if (!currentTask) return false;
    
    const taskNum = currentTask.taskNumber;
    if (taskNum === 1) {
      return isIeltsWritingSubmited?.isTask1Submitted || false;
    } else if (taskNum === 2) {
      return isIeltsWritingSubmited?.isTask2Submitted || false;
    }
    return false;
  })();

  // Check if all tasks are submitted (for disabling Finish button in practice mode)
  // Only check tasks that actually exist in writingTasks array
  const allTasksSubmitted = (() => {
    if (writingTasks.length === 0) return false;
    
    return writingTasks.every(task => {
      const taskNum = task.taskNumber;
      if (taskNum === 1) {
        return isIeltsWritingSubmited?.isTask1Submitted || false;
      } else if (taskNum === 2) {
        return isIeltsWritingSubmited?.isTask2Submitted || false;
      }
      return false;
    });
  })();

  // Get current task data
  const currentTask = writingTasks.length > 0 ? writingTasks[currentTaskIndex] : null;
  
  // Extract case notes from current task or fallback to writingData
  let caseNotes = [];
  let writingPrompt = "";
  
  console.log("WritingSection props:", { writingData, writingTasks, currentTaskIndex });
  console.log("writingData.caseNotes:", writingData?.caseNotes);
  
  if (currentTask) {
    // For IELTS writing tasks, use task instructions as "case notes"
    console.log("Current task data:", currentTask);
    
    // Create case notes from task instructions and prompt
    caseNotes = [];
    
    // If no case notes exist, create them from task content for IELTS
    if ((!writingData?.caseNotes || writingData.caseNotes.length === 0) && currentTask) {
      const taskSubSections = [];
      
      // Add instructions if available
      if (currentTask.instructions) {
        taskSubSections.push({
          _id: `${currentTask._id}_instructions`,
          label: "Instructions",
          content: currentTask.instructions
        });
      }
      
      // Add task content
      if (currentTask.task) {
        taskSubSections.push({
          _id: `${currentTask._id}_task`,
          label: "Task",
          content: currentTask.task
        });
      }
      
      // Add image if available
      if (currentTask.imageURL) {
        taskSubSections.push({
          _id: `${currentTask._id}_image`,
          label: "Reference Image",
          content: `<img src="${currentTask.imageURL}" alt="Task Reference Image" style="max-width: 100%; height: auto; border-radius: 8px; margin: 10px 0;" />`
        });
      }
      
      caseNotes = [{
        _id: currentTask._id,
        title: currentTask.taskTitle || "Writing Task",
        content: "", // Keep title separate from content
        subSections: taskSubSections
      }];
    } else {
      caseNotes = writingData?.caseNotes || currentTask.caseNotes || [];
    }
    
    writingPrompt = currentTask.task || "";
    console.log("Case notes created/found:", caseNotes);
  } else {
    // Fallback to original writingData structure
    const data = writingData || {};
    caseNotes = data.caseNotes || [];
    writingPrompt = data.writingPrompt || "";
    console.log("Case notes from writingData:", caseNotes);
  }
  
  // Use current task data if available, fallback to writingData
  const taskData = currentTask || { task: writingPrompt, taskTitle: "Writing Task", instructions: "" };
  const { task: currentTaskPrompt, taskTitle, instructions: taskInstructions } = taskData;

  // Extract reading time from instructions (default to 5 minutes if not found)
  const getReadingTimeFromInstructions = () => {
    // Skip preparation time in practice mode
    if (mode === "practice") {
      return 0;
    }
    
    const instructionsText = writingData?.instructions || "";
    const readingTimeMatch = instructionsText.match(
      /READING TIME:\s*(\d+)\s*MINUTES?/i
    );
    return readingTimeMatch ? parseInt(readingTimeMatch[1]) : 5; // Default to 5 minutes
  };

  // Notes functionality
  const [notes, setNotes] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  
  // Header visibility state
  const [headerVisible, setHeaderVisible] = useState(true);
  
  // Zoom level states for left and right panels
  const [leftZoom, setLeftZoom] = useState(100);
  const [rightZoom, setRightZoom] = useState(100);

  const handleAddNote = (noteData) => {
    const taskLabel = currentTask ? 
      (currentTask.taskTitle || `Task ${currentTask.taskNumber || (currentTaskIndex + 1)}`) : 
      "Writing Task";
    
    setNotes((prevNotes) => [
      ...prevNotes,
      {
        ...noteData,
        timestamp: new Date().toISOString(),
        part: taskLabel,
      },
    ]);
  };

  // Preparation time logic with API integration - disable for simulation mode
  const PREP_TIME_MINUTES = mode === "practice" || mode === "simulation" ? 0 : getReadingTimeFromInstructions();
  const PREP_TIME_SECONDS = PREP_TIME_MINUTES * 60;
  const [prepRemaining, setPrepRemaining] = useState(PREP_TIME_SECONDS);
  const [prepActive, setPrepActive] = useState(PREP_TIME_SECONDS > 0);
  const [skippedTime, setSkippedTime] = useState(0); // Time skipped in seconds
  const [adjustedEndTime, setAdjustedEndTime] = useState(endTime); // Adjusted end time after skipping

  // Navigation hook
  const navigate = useNavigate();
  const location = useLocation();

  // Handle browser back button - navigate to main screen
  useEffect(() => {
    if (!testId) return; // Only if testId is provided

    const handlePopState = (event) => {
      // Prevent default back navigation
      event.preventDefault();
      // Detect test type from URL
      const path = location.pathname.toLowerCase();
      let backPath;
      if (bundleId) {
        backPath = `/app/bundles/${bundleId}`;
      } else if (path.includes('/mini-mock-test/')) {
        backPath = `/mini-mock-test/${testId}`;
      } else if (path.includes('/class-test/')) {
        backPath = `/class-test/${testId}`;
      } else {
        backPath = `/mock-test/${testId}`;
      }
      navigate(backPath, { replace: true });
    };

    // Add popstate event listener
    window.addEventListener("popstate", handlePopState);

    // Push current state to allow back button to trigger our handler
    window.history.pushState(null, "", window.location.pathname);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [testId, bundleId, navigate]);

  // Timer effect for API-based timing (startTime/endTime)
  const [timerPaused, setTimerPaused] = useState(false);
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  // Text highlighting & notes functionality now handled by TextHighlighterWrapper

  // Modal & progress state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const intervalRef = useRef(null);

  // For paper-based: image upload - now stored per task index
  const [uploadedImagesByTask, setUploadedImagesByTask] = useState({});
  
  // Get current task's uploaded images
  const uploadedImages = uploadedImagesByTask[currentTaskIndex] || [];

  // API hooks
  const [createWritingAnswer] = useCreateWritingAnswerMutation();
  const [uploadWritingImages] = useUploadWritingImagesMutation();
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const currentImages = uploadedImagesByTask[currentTaskIndex] || [];

    // If already 2 or more images uploaded for current task, prevent further upload
    if (currentImages.length >= 2) {
      alert("You can upload only 2 images per task.");
      return;
    }

    // Limit new uploads to 2 - existing count
    const allowedCount = 2 - currentImages.length;
    const limitedFiles = files.slice(0, allowedCount);

    const newImages = limitedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setUploadedImagesByTask((prev) => ({
      ...prev,
      [currentTaskIndex]: [...currentImages, ...newImages],
    }));
  };

  const handleRemoveImage = (indexToRemove) => {
    const currentImages = uploadedImagesByTask[currentTaskIndex] || [];
    setUploadedImagesByTask((prev) => ({
      ...prev,
      [currentTaskIndex]: currentImages.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  // Submit logic (calls onSubmit with either text or images)
  const handleConfirmSubmit = useCallback(async () => {
    setConfirmOpen(false);
    setUploading(true);
    setUploadProgress(0);
    intervalRef.current = setInterval(
      () => setUploadProgress((p) => Math.min(p + 5, 100)),
      200
    );

    try {
      if (submissionType === "computer") {
        // For computer-based submission, send to writing API
        console.log("Props received:", {
          studentAttemptId,
          writingTestId,
          isAiBased,
          writingTasks,
          taskAnswers,
        });

        // Build tasks array with answers
        const tasksWithAnswers = writingTasks.map((task, index) => {
          const taskAnswer = taskAnswers[index] || "";
          return {
            taskNumber: task.taskNumber || (index + 1),
            taskType: task.taskType || (task.taskNumber === 1 ? "letter" : "essay"), // Default types based on task number
            writingTaskId: task._id,
            writingText: taskAnswer,
            writingImage: [],
            isAiBased: true,
            isPictureBased: false
          };
        });

        const payload = {
          studentTestAttemptId: studentAttemptId,
          writingTestId: writingTestId,
          tasks: tasksWithAnswers,
          isAiBased: true,
        };

        console.log("Submitting writing answer with tasks:", payload);

        // Call the actual API
        const result = await createWritingAnswer(payload).unwrap();
        console.log("Writing submission successful:", result);

        // After successful submission, fetch test attempt results only if needed
        if (shouldFetchResults) {
          console.log("Fetching test attempt results for:", studentAttemptId);
          try {
            const resultsResponse = await getTestAttemptResults(
              studentAttemptId
            ).unwrap();
            console.log("Test attempt results:", resultsResponse);
            // Call the callback if provided
            if (onResultsReceived) {
              onResultsReceived(resultsResponse);
            }
          } catch (resultsError) {
            console.error("Error fetching test attempt results:", resultsError);
          }
        } else {
          console.log(
            "Skipping test results fetch - not needed for this submission"
          );
        }
      } else if (submissionType === "paper") {
        // For paper-based submission, first upload images for all tasks, then submit
        console.log("Paper-based submission - uploading images first");

        // Collect all images from all tasks
        const allImages = [];
        const imageCountPerTask = {};
        
        writingTasks.forEach((task, index) => {
          const taskImages = uploadedImagesByTask[index] || [];
          imageCountPerTask[index] = taskImages.length;
          allImages.push(...taskImages);
        });

        if (allImages.length === 0) {
          throw new Error("No images uploaded for paper-based submission");
        }

        // Prepare FormData for image upload
        const formData = new FormData();
        allImages.forEach((img) => {
          formData.append("writingImages", img.file);
        });

        console.log("Uploading images:", allImages.length, "files");

        // Upload images first
        const uploadResult = await uploadWritingImages(formData).unwrap();
        console.log("Image upload successful:", uploadResult);

        // Extract the image data from the response
        const imageData =
          uploadResult?.data?.uploadToBucket ||
          uploadResult?.uploadToBucket ||
          [];

        if (!Array.isArray(imageData) || imageData.length === 0) {
          throw new Error("No image data received from upload API");
        }

        // Distribute uploaded images back to their respective tasks
        let imageIndex = 0;
        const tasksWithImages = writingTasks.map((task, index) => {
          const taskImageCount = imageCountPerTask[index] || 0;
          const taskImageData = imageData.slice(imageIndex, imageIndex + taskImageCount);
          imageIndex += taskImageCount;
          
          return {
            taskNumber: task.taskNumber || (index + 1),
            taskType: task.taskType || (task.taskNumber === 1 ? "letter" : "essay"), // Default types based on task number
            writingTaskId: task._id,
            writingText: "", // Empty for paper-based
            writingImage: taskImageData, // Only this task's images
            isAiBased: true,
            isPictureBased: taskImageData.length > 0
          };
        });

        const payload = {
          studentTestAttemptId: studentAttemptId,
          writingTestId: writingTestId,
          tasks: tasksWithImages,
          isAiBased: true,
        };

        console.log("Submitting writing answer with images:", payload);

        // Submit with image data
        const result = await createWritingAnswer(payload).unwrap();
        console.log("Writing submission successful:", result);

        // After successful submission, fetch test attempt results only if needed
        if (shouldFetchResults) {
          console.log("Fetching test attempt results for:", studentAttemptId);
          try {
            const resultsResponse = await getTestAttemptResults(
              studentAttemptId
            ).unwrap();
            console.log("Test attempt results:", resultsResponse);
            if (onResultsReceived) {
              onResultsReceived(resultsResponse);
            }
          } catch (resultsError) {
            console.error("Error fetching test attempt results:", resultsError);
          }
        } else {
          console.log(
            "Skipping test results fetch - not needed for this submission"
          );
        }
      }

      // ✅ For SIMULATION mode: update test attempt completion status
      if (mode === "simulation" && updateTestAttempt && studentAttemptId) {
        try {
          console.log(
            "Updating test attempt completion for simulation mode:",
            studentAttemptId
          );
          await updateTestAttempt({
            studentAttemptId,
            isCompleted: true,
          });
          console.log("Test attempt updated successfully");
        } catch (error) {
          console.error("Error updating test attempt:", error);
          // Don't fail the submission if this update fails
        }
      }

      // ✅ For PRACTICE mode: update test attempt completion status
      if (mode === "practice" && updateTestAttempt && studentAttemptId) {
        try {
          console.log(
            "Updating test attempt completion for practice mode:",
            studentAttemptId
          );
          await updateTestAttempt({
            studentAttemptId,
            isCompleted: true,
          });
          console.log("Practice test attempt updated successfully");
        } catch (error) {
          console.error("Error updating practice test attempt:", error);
          // Don't fail the submission if this update fails
        }
      }

      // ✅ Only for PRACTICE mode: call onFinish after successful submit
      if (mode === "practice" && isLastQuestion && onFinish) {
        onFinish();
      }

      setTimeout(() => {
        clearInterval(intervalRef.current);
        setUploadProgress(100);
        setTimeout(() => {
          setUploading(false);
        }, 300);
      }, 1000);
    } catch (error) {
      console.error("Error submitting writing answer:", error);
      clearInterval(intervalRef.current);
      setUploading(false);
      alert("Failed to submit writing answer. Please try again.");
    }
  }, [
    submissionType,
    studentAttemptId,
    writingTestId,
    isAiBased,
    answer,
    shouldFetchResults,
    onResultsReceived,
    createWritingAnswer,
    getTestAttemptResults,
    uploadedImagesByTask,
    uploadWritingImages,
    mode,
    updateTestAttempt,
    isLastQuestion,
    onFinish,
    writingTasks,
    taskAnswers,
  ]);

  // Expose submission function to parent component
  useImperativeHandle(
    ref,
    () => ({
      submitAnswer: handleConfirmSubmit,
    }),
    [handleConfirmSubmit]
  );

  // Timer effect - moved after handleConfirmSubmit to avoid hoisting issues
  useEffect(() => {
    if (!adjustedEndTime) return;

    const updateApiTimer = () => {
      if (timerPaused || autoSubmitted) return; // Don't update when paused or already submitted

      const now = new Date();
      const endDateTime = new Date(adjustedEndTime);
      const remainingMs = endDateTime.getTime() - now.getTime();
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

      if (remainingSeconds <= 0) {
        // Notify parent that time is up
        if (onTimeUp) {
          onTimeUp();
        }

        // Auto-trigger submit only in simulation mode, not in practice mode
        if (mode === "simulation" && answer && answer.trim().length > 0) {
          console.log("Timer ended - auto-triggering submit in simulation mode");
          setAutoSubmitted(true); // Prevent repeated calls
          handleConfirmSubmit();
        }
      }
    };

    // Update immediately
    updateApiTimer();

    // Then update every second
    const interval = setInterval(updateApiTimer, 1000);

    // Listen for pause timer event
    const handlePauseTimer = () => {
      setTimerPaused(true);
    };

    window.addEventListener("pauseTestTimers", handlePauseTimer);

    return () => {
      clearInterval(interval);
      window.removeEventListener("pauseTestTimers", handlePauseTimer);
    };
  }, [adjustedEndTime, onTimeUp, timerPaused, answer, handleConfirmSubmit, autoSubmitted]);

  const [showTask] = useState(true);

  // Sync current task answer with the answer prop when task changes
  useEffect(() => {
    if (taskAnswers[currentTaskIndex] !== undefined) {
      // Load the answer for the current task
      const taskAnswer = taskAnswers[currentTaskIndex] || "";
      if (onAnswerChange && taskAnswer !== answer) {
        console.log("Loading task answer:", taskAnswer, "for task:", currentTaskIndex);
        onAnswerChange(taskAnswer);
      }
    }
  }, [currentTaskIndex, taskAnswers, onAnswerChange]); // Remove 'answer' from dependencies to avoid circular updates

  // Update the adjustedEndTime when endTime changes
  useEffect(() => {
    setAdjustedEndTime(endTime);
  }, [endTime]);

  // Enhanced skip preparation handler
  const handleSkipPrep = () => {
    if (!prepActive) return;

    const timeSkipped = prepRemaining; // How much time was skipped
    setSkippedTime(timeSkipped);
    setPrepActive(false);

    // Adjust the end time by subtracting the skipped preparation time
    if (adjustedEndTime) {
      const currentEndTime = new Date(adjustedEndTime);
      const newEndTime = new Date(
        currentEndTime.getTime() - timeSkipped * 1000
      );
      const newEndTimeISO = newEndTime.toISOString();
      setAdjustedEndTime(newEndTimeISO);

      // Notify parent component about the adjusted end time
      if (onEndTimeAdjustment) {
        console.log(
          "📤 WritingSection: Calling parent callback with adjusted end time:",
          newEndTimeISO
        );
        onEndTimeAdjustment(newEndTimeISO);
        console.log("📤 WritingSection: Parent callback called successfully");
      } else {
        console.warn(
          "⚠️ WritingSection: onEndTimeAdjustment callback not provided"
        );
      }

      console.log(
        `Preparation time skipped: ${Math.floor(timeSkipped / 60)}m ${
          timeSkipped % 60
        }s`
      );
      console.log(`Original end time: ${currentEndTime.toISOString()}`);
      console.log(`New end time: ${newEndTimeISO}`);
    }
  };

  // Enhanced preparation time countdown
  useEffect(() => {
    if (!prepActive) return;
    if (prepRemaining <= 0) {
      setPrepActive(false);
      return;
    }
    const interval = setInterval(() => {
      setPrepRemaining((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [prepActive, prepRemaining]);

  const fmtPrepClock = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  // Timer calculation logic similar to ReadingSection
  const getWritingTimeRemaining = () => {
    if (!adjustedEndTime) return 0;
    const now = new Date();
    const endDateTime = new Date(adjustedEndTime);
    const remainingMs = endDateTime.getTime() - now.getTime();
    return Math.max(0, Math.floor(remainingMs / 1000));
  };

  const getWritingTotalTime = () => {
    if (!startTime || !adjustedEndTime) return 1;
    const startDateTime = new Date(startTime);
    const endDateTime = new Date(adjustedEndTime);
    return Math.max(1, Math.floor((endDateTime.getTime() - startDateTime.getTime()) / 1000));
  };

  const writingTimeRemaining = getWritingTimeRemaining();
  const writingTotalTime = getWritingTotalTime();
  const timerProgress = writingTotalTime > 0 ? ((writingTotalTime - writingTimeRemaining) / writingTotalTime) * 100 : 0;

  // Format timer display
  const formatTimerDisplay = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      
      {/* Header Section */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          bgcolor: "#F7F7F7",
          px: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0.5 },
          py: { xs: 0.1 },
          position: "sticky",
          top: 0,
          zIndex: 1000,
          display: headerVisible ? "block" : "none",
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Card
          elevation={1}
          sx={{
            width: "100%",
            mb: 1,
            p: { xs: 0.2, sm: 0.8, md: 1, lg: 1.2 },
            bgcolor: "#fff",
            borderRadius: { xs: 0.8, sm: 1.5, md: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              gridTemplateAreas: {
                xs: `"left" "center" "right"`,
                sm: `"left center right"`,
                md: `"left center right"`,
                lg: `"left center right"`,
              },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 2fr 1fr",
                md: "1fr 2fr 1fr", 
                lg: "1fr 2fr 1fr",
              },
              gridTemplateRows: { xs: "auto auto auto", sm: "auto" },
              columnGap: { xs: 0, sm: 0.8, md: 1.2, lg: 1.5 },
              rowGap: { xs: 0.3, sm: 0 },
              minWidth: 0,
              minHeight: { xs: 20, sm: 45, md: 50 },
            }}
          >
            {/* LEFT - Writing Info */}
            <Box
              sx={{
                gridArea: "left",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                gap: { xs: 0.3, sm: 1, md: 1.2, lg: 1.5 },
                py: { xs: 0.1, sm: 0.2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 0,
                  flexShrink: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#9D2235",
                    fontSize: { xs: "0.6rem", sm: "0.85rem", md: "0.95rem" },
                    lineHeight: 1.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Writing Module
                </Typography>
              </Box>
            </Box>

            {/* CENTER - Writing Title and Time Information */}
            <Box
              sx={{
                gridArea: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: { xs: 0.2, sm: 0.8, md: 1.2, lg: 1.5 },
                minWidth: 0,
                justifyContent: "center",
                py: { xs: 0.1, sm: 0.3, md: 0.4 },
              }}
            >
          <Box sx={{ 
            display: "flex", 
            gap: 1, 
            alignItems: "center",
          }}>
            {console.log("🔘 Task Navigation Debug:", { 
              writingTasksLength: writingTasks.length, 
              shouldShowButtons: writingTasks.length > 1,
              writingTasks: writingTasks,
              currentTaskIndex 
            })}
            {writingTasks.length > 1 && writingTasks.map((task, index) => {
              // Task navigation buttons should never be disabled
              // Only the text field gets disabled for submitted tasks
              
              return (
                <Button
                  key={task._id || index}
                  onClick={() => onTaskChange && onTaskChange(index)}
                  variant={index === currentTaskIndex ? "contained" : "outlined"}
                  size="small"
                  sx={{
                    minWidth: "auto",
                    px: { xs: 1.5, sm: 2 },
                    py: 0.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 600,
                    borderRadius: 2,
                    ...(index === currentTaskIndex
                      ? {
                          backgroundColor: "#9D2235",
                          color: "white",
                          "&:hover": { backgroundColor: "#8B1A2E" },
                        }
                      : {
                          borderColor: "#9D2235",
                          color: "#9D2235",
                          "&:hover": { backgroundColor: "#9D223520" },
                        }),
                  }}
                >
                  Writing Section {task.taskNumber || index + 1}
                </Button>
              );
            })}
          </Box>

            </Box>
 
            {/* RIGHT - Circle Clock, Timer and Notes */}
            <Box
              sx={{
                gridArea: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.3, sm: 0.6, md: 0.8 },
              }}
            >
              {/* Circle Clock - Above everything */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <EnhancedCountdown
                  size={20}
                  stroke={2}
                  progress={timerProgress}
                  showPie
                  showRing
                  idSuffix="writingTime"
                />
              </Box>
              
              {/* Timer and Notes Row - Below circle */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 0.4, sm: 0.8, md: 1 },
                }}
              >
                {/* Timer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 0.6, md: 0.7 },
                    px: { xs: 0.8, sm: 1, md: 1.2 },
                    py: { xs: 0.4, sm: 0.5, md: 0.6 },
                    borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                    minWidth: { xs: 70, sm: 90, md: 110 },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <AccessTimeIcon 
                    sx={{ 
                      fontSize: { xs: 16, sm: 20, md: 24 }, 
                      color: "#9D2235",
                    }} 
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
                      fontWeight: 800,
                      color: "#9D2235",
                      fontFamily: "'Roboto Mono', 'Courier New', monospace",
                      letterSpacing: { xs: 0.4, sm: 0.6, md: 0.8 },
                      lineHeight: 1,
                    }}
                  >
                    {formatTimerDisplay(writingTimeRemaining)}
                  </Typography>
                </Box>

                {/* Notes Icon */}
                <Badge
                  badgeContent={notes?.length || 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#fff",
                      color: "#9D2235",
                      fontWeight: "bold",
                      fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.5rem" },
                      minWidth: { xs: 6, sm: 10, md: 12 },
                      height: { xs: 6, sm: 10, md: 12 },
                      boxShadow: "0 2px 4px rgba(157,34,53,0.2)",
                    },
                  }}
                >
                  <IconButton
                    onClick={() => setNotesModalOpen(true)}
                    sx={{
                      background: "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.1)",
                      boxShadow: "0 4px 12px rgba(157,34,53,0.25), 0 2px 4px rgba(157,34,53,0.15)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": { 
                        background: "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 16px rgba(157,34,53,0.3), 0 4px 8px rgba(157,34,53,0.2)",
                      },
                      width: { xs: 18, sm: 28, md: 32 },
                      height: { xs: 18, sm: 28, md: 32 },
                    }}
                  >
                    <StickyNote2Icon
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.9rem", md: "1rem" },
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                      }}
                    />
                  </IconButton>
                </Badge>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>

  




{      /* ===== Split‑screen ===== */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
            {/* left: case notes */}
            <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: { xs: "50%", md: "100%" },
            p: { xs: 1, sm: 2 },
            overflow: "auto",
            borderRight: { md: "2px solid #e0e0e0" },
            borderBottom: { xs: "2px solid #e0e0e0", md: "none" },
            background: "#fff",
            position: "relative",
          }}
            >
          {/* Zoom Controls - Left Panel */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 0.5,
              mb: 2,
              bgcolor: "rgba(255,255,255,0.98)",
              backdropFilter: "blur(12px)",
              px: 1.5,
              py: 1,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid rgba(157, 34, 53, 0.1)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#9D2235",
                fontWeight: 700,
                fontSize: "0.875rem",
              }}
            >
              Writing Task {currentTaskIndex + 1}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setLeftZoom(prev => Math.max(70, prev - 10))}
              disabled={leftZoom <= 70}
              sx={{
                width: 32,
                height: 32,
                bgcolor: leftZoom <= 70 ? "#f1f5f9" : "white",
                border: "1px solid #e2e8f0",
                "&:hover": {
                  bgcolor: leftZoom <= 70 ? "#f1f5f9" : "#9D2235",
                  color: "white",
                  borderColor: "#9D2235",
                },
                "&:disabled": {
                  opacity: 0.4,
                  cursor: "not-allowed",
                },
              }}
            >
              <ZoomOut sx={{ fontSize: 18 }} />
            </IconButton>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                bgcolor: "#f8fafc",
                borderRadius: 1,
                minWidth: 55,
                textAlign: "center",
                fontSize: "0.813rem",
                fontWeight: 700,
                color: "#9D2235",
                border: "1px solid #e2e8f0",
              }}
            >
              {leftZoom}%
            </Box>
            <IconButton
              size="small"
              onClick={() => setLeftZoom(prev => Math.min(150, prev + 10))}
              disabled={leftZoom >= 150}
              sx={{
                width: 32,
                height: 32,
                bgcolor: leftZoom >= 150 ? "#f1f5f9" : "white",
                border: "1px solid #e2e8f0",
                "&:hover": {
                  bgcolor: leftZoom >= 150 ? "#f1f5f9" : "#9D2235",
                  color: "white",
                  borderColor: "#9D2235",
                },
                "&:disabled": {
                  opacity: 0.4,
                  cursor: "not-allowed",
                },
              }}
            >
              <ZoomIn sx={{ fontSize: 18 }} />
            </IconButton>
            </Box>
          </Box>
          <TextHighlighterWrapper onAddNote={handleAddNote} notes={notes}>
           
            {caseNotes.map((note, idx) => (
              <Card
            key={note._id || idx}
            elevation={0}
            sx={{
              mb: { xs: 2, sm: 3 },
              p: { xs: 1.5, sm: 3 },
              borderRadius: 2,
              boxShadow: "none",
              background: "#fff",
            }}
              >
            {note.title && (
              <HtmlRenderer
                content={note.title}
                component="h6"
                sx={{
              fontWeight: 700,
              mb: { xs: 1.5, sm: 2 },
              color: "#9D2235",
              fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '1rem' : '1.25rem'})`,
                }}
              />
            )}
            
            {(!note.subSections || note.subSections.length === 0) &&
              note.content && (
                <HtmlRenderer
              content={note.content}
              component="div"
              sx={{
                mb: { xs: 1.5, sm: 2 },
                lineHeight: { xs: 1.6, sm: 1.8 },
                whiteSpace: "pre-line",
                fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
              }}
                />
              )}

            {note.subSections?.length > 0 && (
              <Box sx={{ ml: 2, mb: 1 }}>
                {note.subSections.length > 1 ? (
              <ul style={{ listStyle: "disc", paddingLeft: 40, margin: 0 }}>
                {note.subSections.map((sub, sidx) => (
                  <li key={sub._id || sidx} style={{ marginBottom: 8 }}>
                {sub.label && (
                  <HtmlRenderer
                    content={`${sub.label}${sub.label ? ": " : ""}`}
                    component="span"
                    sx={{
                  fontWeight: 500,
                  fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                    }}
                  />
                )}
                {sub.content && (
                  <HtmlRenderer
                    content={sub.content}
                    component="span"
                    sx={{
                  fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                    }}
                  />
                )}
                  </li>
                ))}
              </ul>
                ) : (
              <Box>
                {note.subSections[0].label && (
                  <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                }}
                  >
                {note.subSections[0].label}
                {note.subSections[0].label ? ": " : ""}
                  </Typography>
                )}
                {note.subSections[0].content && (
                  <HtmlRenderer
                content={note.subSections[0].content}
                component="div"
                sx={{
                  fontSize: `calc((${leftZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                }}
                  />
                )}
              </Box>
                )}
              </Box>
            )}
              </Card>
            ))}
          </TextHighlighterWrapper>
            </Box>

            
            {/* right: writing task */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              height: { xs: "50%", md: "100%" },
              pl: { xs: 1, sm: 2 },
              pr: { xs: 1, sm: 2 },
              overflow: "auto",
              background: "#f8f9fa",
            }}
          >
            {/* Zoom Controls - Right Panel */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 0.5,
                mb: 2,
                bgcolor: "rgba(255,255,255,0.98)",
                backdropFilter: "blur(12px)",
                px: 1.5,
                py: 1,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid rgba(157, 34, 53, 0.1)",
              }}
            >
           
              <IconButton
                size="small"
                onClick={() => setRightZoom(prev => Math.max(70, prev - 10))}
                disabled={rightZoom <= 70}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: rightZoom <= 70 ? "#f1f5f9" : "white",
                  border: "1px solid #e2e8f0",
                  "&:hover": {
                    bgcolor: rightZoom <= 70 ? "#f1f5f9" : "#9D2235",
                    color: "white",
                    borderColor: "#9D2235",
                  },
                  "&:disabled": {
                    opacity: 0.4,
                    cursor: "not-allowed",
                  },
                }}
              >
                <ZoomOut sx={{ fontSize: 18 }} />
              </IconButton>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  bgcolor: "#f8fafc",
                  borderRadius: 1,
                  minWidth: 55,
                  textAlign: "center",
                  fontSize: "0.813rem",
                  fontWeight: 700,
                  color: "#9D2235",
                  border: "1px solid #e2e8f0",
                }}
              >
                {rightZoom}%
              </Box>
              <IconButton
                size="small"
                onClick={() => setRightZoom(prev => Math.min(150, prev + 10))}
                disabled={rightZoom >= 150}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: rightZoom >= 150 ? "#f1f5f9" : "white",
                  border: "1px solid #e2e8f0",
                  "&:hover": {
                    bgcolor: rightZoom >= 150 ? "#f1f5f9" : "#9D2235",
                    color: "white",
                    borderColor: "#9D2235",
                  },
                  "&:disabled": {
                    opacity: 0.4,
                    cursor: "not-allowed",
                  },
                }}
              >
                <ZoomIn sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <TextHighlighterWrapper onAddNote={handleAddNote} notes={notes}>
              {/* Writing Task Navigation - moved to top outside card */}
        
              
              <Card
                elevation={2}
                sx={{
                  mb: { xs: 2, sm: 3 },
                  p: { xs: 1.5, sm: 3 },
                  borderRadius: 2,
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                  background: "#fff",
                }}
              >
              

                {/* Writing Area */}
                {submissionType === "computer" && mode !== "readonly" && (
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      multiline
                      rows={12}
                      fullWidth
                      value={answer}
                      onChange={(e) => onAnswerChange(e.target.value)}
                      placeholder={isCurrentTaskSubmitted ? "This task has already been submitted." : "Type your answer..."}
                      disabled={isCurrentTaskSubmitted}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: `calc((${rightZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                          borderRadius: 2,
                          paddingBottom: "40px", // Add padding to prevent text overlap with word count
                          ...(isCurrentTaskSubmitted && {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          }),
                        },
                        "& .MuiOutlinedInput-input": {
                          paddingBottom: "8px", // Reduce input padding since we added container padding
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        position: "absolute",
                        bottom: 12,
                        right: 16,
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        bgcolor: "rgba(255,255,255,0.95)",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        zIndex: 10,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        border: "1px solid rgba(0,0,0,0.1)",
                        fontWeight: 500,
                      }}
                    >
                      {answer.trim().split(/\s+/).filter(Boolean).length}/200 words
                    </Typography>
                    {prepActive && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            width: 24,
                            height: 24,
                            color: "#9D2235",
                            opacity: 0.8,
                          }}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 11V7a4 4 0 118 0v4M6 11V7a6 6 0 1112 0v4m-6 4v2m0 0h.01"
                          />
                        </svg>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Paper-based submission */}
                {submissionType === "paper" && (
                  <Box sx={{ textAlign: "center", py: { xs: 2, sm: 4 } }}>
                    <Typography
                      sx={{
                        fontSize: `calc((${rightZoom / 100}) * ${window.innerWidth < 600 ? '1rem' : '1.125rem'})`,
                        fontWeight: 600,
                        color: "#9D2235",
                        mb: 2,
                      }}
                    >
                      Paper-Based Submission
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: `calc((${rightZoom / 100}) * ${window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                        color: "text.secondary",
                        mb: 2,
                      }}
                    >
                      Please write your answer on paper and upload images when complete.
                    </Typography>

                    {/* Preparation Time Warning */}
                    {prepActive && (
                      <Box
                        sx={{
                          p: 2,
                          mb: 3,
                          bgcolor: "#fff3cd",
                          border: "1px solid #ffeaa7",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                            color: "#856404",
                            fontWeight: 500,
                            textAlign: "center",
                          }}
                        >
                          📝 Image upload will be available after preparation time ends or is skipped
                        </Typography>
                      </Box>
                    )}
                    
                    {uploadedImages.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                          Uploaded Images:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                          {uploadedImages.map((img, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                position: "relative",
                                width: 100,
                                height: 100,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                overflow: "hidden",
                              }}
                            >
                              <img
                         src={img.preview}
                                alt={`Upload ${idx + 1}`}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                              <IconButton
                                onClick={() => handleRemoveImage(idx)}
                                sx={{
                                  position: "absolute",
                                  top: 2,
                                  right: 2,
                                  backgroundColor: "rgba(157, 34, 53, 0.8)",
                                  color: "white",
                                  width: 20,
                                  height: 20,
                                  minWidth: 20,
                                  "&:hover": {
                                    backgroundColor: "rgba(157, 34, 53, 1)",
                                  },
                                }}
                                size="small"
                              >
                                <CloseIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: "none" }}
                      id="image-upload"
                      disabled={isCurrentTaskSubmitted}
                    />
                    <label htmlFor="image-upload">
                      <Button
                        component="span"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        disabled={isCurrentTaskSubmitted}
                        sx={{
                          bgcolor: "#9D2235",
                          color: "white",
                          "&:hover": { 
                            bgcolor: "#7a182a" 
                          },
                          cursor: isCurrentTaskSubmitted ? "not-allowed" : "pointer",
                          "&.Mui-disabled": {
                            bgcolor: "rgba(0, 0, 0, 0.12)",
                            color: "rgba(0, 0, 0, 0.26)",
                          },
                        }}
                      >
                        Select Images
                      </Button>
                    </label>
                  </Box>
                )}
              </Card>
            </TextHighlighterWrapper>
          </Box>
        </Box>

      {/* Notes Modal */}
      <NotesModal
        open={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        notes={notes}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Box sx={{ bgcolor: "white", borderRadius: 2, boxShadow: 3, p: 3, width: "100%", maxWidth: 400, mx: "auto", mt: 24 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#9D2235" }}>
            Finish Writing Test
          </Typography>
          <Typography sx={{ mb: 4, color: "text.secondary" }}>
            Are you sure you want to finish and submit your answer? This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              variant="outlined"
              sx={{ px: 2, py: 1, borderColor: "#e0e0e0", color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSubmit}
              variant="contained"
              sx={{ px: 2, py: 1, bgcolor: "#9D2235", "&:hover": { bgcolor: "#7a182a" } }}
            >
              Finish
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Upload Progress */}
      <Backdrop open={uploading} sx={{ zIndex: 9999 }}>
        <Box sx={{ bgcolor: "white", borderRadius: 2, boxShadow: 3, p: 3, width: "100%", maxWidth: 400, mx: "auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#9D2235" }}>
            {submissionType === "paper" ? "Uploading Images..." : "Submitting Answer..."}
          </Typography>
          <Box sx={{ width: "100%", height: 8, bgcolor: "#e0e0e0", borderRadius: 1, overflow: "hidden", mb: 2 }}>
            <Box
              sx={{
                height: "100%",
                bgcolor: "#9D2235",
                borderRadius: 1,
                width: `${uploadProgress}%`,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
          <Typography sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
            {uploadProgress}%
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
});
